/**
 * HeroCutIn.js
 * Implements the Hero cut-in
 */

import * as PIXI from "pixi.js";

import { CoreEvents, Game, GameTimer, GameTween, GameWindow, KeyboardKey, UiElement, UiImage, UiText, WindowLayers } from "pixi_core";
import { Hero } from "../Heroes/Hero";
import { Common } from "../game/Common";

import {
    CheatEvents,
    GameEvents,
    Heroes,
    REF_RESOLUTION_HEIGHT,
    TEAM_SIZE,
    HEROES_NAME_TO_ID,
    NB_HEROES,
    HEROES_ID_TO_NAME,
    HERO_ABILITY_NAMES
} from "../Constants";

export class HeroCutIn extends GameWindow {
    /**
     * @public
     */
    constructor() {
        super({
            name: "HeroCutIn",
            disableInputUnder: false,
            showDarkenUnder: false,
            ignoreOutsideZoneTap: true,
            ignoreGearButton: true,
            ignoreAndroidBackButton: true,
            defaultInstantClose: true,
            defaultInstantOpen: true,
            layer: WindowLayers.eUNDER_GEAR
        });

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {Hero[]} */
        this._heroes = [];

        /** @private @type {UiImage} */
        this._abilityCutIn = null;

        /** @private @type {UiText} */
        this._lblCutInAbility = null;

        /** @private @type {UiImage} */
        this._imgAllOutBackground = null;

        /** @private @type {Object} */
        this._alloutBackgroundStartPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._alloutBackgroundOriginalPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._alloutBackgroundFinalPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._cutInFinalPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._cutInStartingPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._textOffset = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._textFinalPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._textStartingPos = { x: 0, y: 0 };

        /** @private @type {GameTween} */
        this._tweenHeroCutIn = null;

        /** @private @type {GameTween} */
        this._tweenHeroCutOut = null;

        /** @private @type {GameTween} */
        this._tweenTextCutIn = null;

        /** @private @type {GameTimer} */
        this._timerHeroCutStop = null;

        /** @private @type {GameTimer} */
        this._timerTextDelay = null;

        /** @private @type {GameTimer} */
        this._timerTextCutStop = null;

        /** @private @type {GameTween} */
        this._tweenTextCutOut = null;

        /** @private @type {GameTween} */
        this._tweenAllOutBackgroundCutIn = null;

        /** @private @type {GameTween} */
        this._tweenAllOutBackgroundStop = null;

        /** @private @type {GameTween} */
        this._tweenAllOutBackgroundCutOut = null;

        /** @private @type {GameTween[]} */
        this._tweenAllOutAttackIn = [null, null, null, null];

        /** @private @type {GameTween[]} */
        this._tweenAllOutAttackStop = [null, null, null, null];

        /** @private @type {GameTween[]} */
        this._tweenAllOutAttackOut = [null, null, null, null];

        /** @private @type {UiImage[]} */
        this._alloutAttackCutIns = [null, null, null, null];

        /** @private @type {Object[]} */
        this._alloutAttackStartPos = [];

        /** @private @type {Object[]} */
        this._alloutAttackOriginalPos = [];

        /** @private @type {Object[]} */
        this._alloutAttackFinalPos = [];

        /** @private @type {Boolean} */
        this._cheatAllOutCompositionActive = false;

        /** @private @type {Number} */
        this._cheatAllOutCompositionSlot = 0;

        /** @private @type {Number} */
        this._cheatAllOutCompositionHeroId = 0;

        Game.on(GameEvents.eHEROES_INITIALIZED, this._onHeroesInitialized, this);
        Game.on(GameEvents.eHERO_USE_ABILITY, this._onUseAbility, this);
        Game.on(GameEvents.eHEROES_ALL_OUT_ATTACK, this._onAllOutAttack, this);
        Game.on(CheatEvents.eALL_OUT_COMPOSITION, this._onCheatAllOutComposition, this);
        Game.on(CoreEvents.eINPUT_KEY_DOWN, this._onCheatAllOutCompositionChange, this);
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("HeroCutIns", this);
        this._abilityCutIn = this._ui.findElementByName("AbilityCutIn");
        this._lblCutInAbility = this._ui.findElementByName("LblAbility");
        this._imgAllOutBackground = this._ui.findElementByName("ImgAllOutAttackBackground");

        this._lblCutInAbility.setLocalized(true);

        const allOutAttack0 = this._ui.findElementByName("ImgAllOutAttack0");
        const allOutAttack1 = this._ui.findElementByName("ImgAllOutAttack1");
        const allOutAttack2 = this._ui.findElementByName("ImgAllOutAttack2");
        const allOutAttack3 = this._ui.findElementByName("ImgAllOutAttack3");
        this._alloutAttackCutIns = [allOutAttack0, allOutAttack1, allOutAttack2, allOutAttack3];

        for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
            this._alloutAttackStartPos.push({
                x: this._alloutAttackCutIns[i].x - 200,
                y: this._alloutAttackCutIns[i].y - REF_RESOLUTION_HEIGHT
            });
            this._alloutAttackOriginalPos.push({ x: this._alloutAttackCutIns[i].x, y: this._alloutAttackCutIns[i].y });
            this._alloutAttackFinalPos.push({
                x: this._alloutAttackCutIns[i].x + 200,
                y: this._alloutAttackCutIns[i].y + REF_RESOLUTION_HEIGHT
            });
        }

        this._alloutBackgroundStartPos = {
            x: this._imgAllOutBackground.x + Common.settings.allOutInXDistance,
            y: this._imgAllOutBackground.y + Common.settings.allOutInYDistance
        };

        this._alloutBackgroundOriginalPos = { x: this._imgAllOutBackground.x, y: this._imgAllOutBackground.y };

        this._alloutBackgroundFinalPos = {
            x: this._imgAllOutBackground.x - Common.settings.allOutInXDistance,
            y: this._imgAllOutBackground.y - Common.settings.allOutInYDistance
        };

        this._cutInFinalPos = { x: this._abilityCutIn.x, y: this._abilityCutIn.y };

        this._cutInStartingPos = {
            x: this._cutInFinalPos.x - this._abilityCutIn.width,
            y: this._cutInFinalPos.y - Common.settings.cutInYDistance
        };

        this._textOffset = { x: this._lblCutInAbility.x, y: this._lblCutInAbility.y };

        this._textFinalPos = { x: this._cutInFinalPos.x + this._textOffset.x, y: this._cutInFinalPos.y + this._textOffset.y };

        this._textStartingPos = { x: this._cutInStartingPos.x + this._textOffset.x, y: this._cutInStartingPos.y + this._textOffset.y };
    }

    /**
     * The window is opening
     * @protected
     * @param {Object} options
     */
    _onWindowOpening(options) {
        this._resetAllOutSprites();
        for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
            this._alloutAttackCutIns[i].setVisible(false);
        }
        this._imgAllOutBackground.setVisible(false);
        this._abilityCutIn.setVisible(false);
        this._lblCutInAbility.setVisible(false);
    }

    /**
     * The window is opened
     * @protected
     */
    _onWindowOpened() {}

    /**
     * The window is closing
     * @protected
     */
    _onWindowClosing(options) {}

    /**
     * The window is closed
     * @protected
     */
    _onWindowClosed() {
        this._destroyCutInTweens();
        this._destroyAllOutTweens();
    }

    /**
     * @private
     * @param {Hero[]} heroes
     * @param {Number} totalHp
     */
    _onHeroesInitialized(heroes, totalHp) {
        this._cheatAllOutCompositionActive = false;
        this._heroes = heroes;
    }

    /**
     * @private
     */
    _resetAllOutSprites() {
        let heroesId = [];
        let reorderedHeroes = [];

        for (let i = 0; i < this._heroes.length; ++i) {
            heroesId.push(HEROES_NAME_TO_ID[this._heroes[i].name]);
        }

        for (let i = 0; i < NB_HEROES; ++i) {
            for (let j = 0; j < heroesId.length; ++j) {
                if (heroesId[j] === i) {
                    reorderedHeroes.push(this._heroes[j].name);
                    break;
                }
            }

            if (reorderedHeroes.length >= TEAM_SIZE) {
                break;
            }
        }

        // The one exception where Iron Man is supposed to go to position 1 instead.
        if (
            reorderedHeroes[0] === Heroes.eBLACK_PANTHER &&
            reorderedHeroes[1] === Heroes.eBLACK_WIDOW &&
            reorderedHeroes[2] === Heroes.eCAPTAIN_AMERICA &&
            reorderedHeroes[3] === Heroes.eIRON_MAN
        ) {
            reorderedHeroes = [Heroes.eIRON_MAN, Heroes.eBLACK_PANTHER, Heroes.eBLACK_WIDOW, Heroes.eCAPTAIN_AMERICA];
        }

        for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
            let sprite = PIXI.Texture.from("IMG_" + reorderedHeroes[i] + "AllOut.png");
            this._alloutAttackCutIns[i].setSprite("IMG_" + reorderedHeroes[i] + "AllOut.png");
            this._alloutAttackCutIns[i].x = this._alloutAttackOriginalPos[i].x;
            this._alloutAttackCutIns[i].y = this._alloutAttackOriginalPos[i].y;
            this._alloutAttackCutIns[i].setSize(sprite.width, sprite.height);
            this._alloutAttackCutIns[i].setVisible(false);
        }
    }

    /**
     * @private
     * @param {String} heroIndex
     */
    _onUseAbility(heroIndex) {
        this.open();
        Game.audio.playSfx("IDSND_CRT_CUT_IN_APPEAR");

        const index = Game.utils.assertNumber(heroIndex, 0);
        const hero = this._heroes[index];

        this._abilityCutIn.setVisible(true);
        let sprite = PIXI.Texture.from("IMG_" + hero.name + "Individual.png");
        this._abilityCutIn.setSprite("IMG_" + hero.name + "Individual.png");
        this._abilityCutIn.setSize(sprite.width, sprite.height);
        this._lblCutInAbility.text = HERO_ABILITY_NAMES[hero.name];
        this._cutInStartingPos.x = this._cutInFinalPos.x - this._abilityCutIn.width;
        this._textStartingPos = { x: this._cutInStartingPos.x + this._textOffset.x, y: this._cutInStartingPos.y + this._textOffset.y };
        this._abilityCutIn.setPosition(this._cutInStartingPos.x, this._cutInStartingPos.y);
        this._lblCutInAbility.setPosition(this._textStartingPos.x, this._textStartingPos.y);

        this._tweenHeroCutIn = Game.windows
            .getCurrentWindow()
            .createTween(this._abilityCutIn)
            .to({ x: this._cutInFinalPos.x, y: this._cutInFinalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                hero.useAbility();
                this._timerHeroCutStop.start();
            });

        this._timerHeroCutStop = Game.windows.getCurrentWindow().createTimer(Common.settings.cutInStopDuration, { autoStart: false });
        this._timerHeroCutStop.onComplete = () => {
            this._tweenHeroCutOut.start();
        };

        this._tweenHeroCutOut = Game.windows
            .getCurrentWindow()
            .createTween(this._abilityCutIn)
            .to({ x: this._cutInStartingPos.x, y: this._cutInStartingPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticIn);

        this._timerTextDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.cutInTextDelay, { autoStart: false });
        this._timerTextDelay.onComplete = () => {
            this._lblCutInAbility.setVisible(true);
            this._tweenTextCutIn.start();
        };

        this._tweenTextCutIn = Game.windows
            .getCurrentWindow()
            .createTween(this._lblCutInAbility)
            .to({ x: this._textFinalPos.x, y: this._textFinalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._timerTextCutStop.start();
            });

        this._timerTextCutStop = Game.windows.getCurrentWindow().createTimer(Common.settings.cutInStopDuration, { autoStart: false });
        this._timerTextCutStop.onComplete = () => {
            this._tweenTextCutOut.start();
        };

        this._tweenTextCutOut = Game.windows
            .getCurrentWindow()
            .createTween(this._lblCutInAbility)
            .to({ x: this._textStartingPos.x, y: this._textStartingPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticIn)
            .onComplete(() => {
                this._onCutInComplete();
            });

        this._tweenHeroCutIn.start();
        this._timerTextDelay.start();
    }

    /**
     * @private
     */
    _onAllOutAttack() {
        this.open();
        Game.audio.playSfx("IDSND_CRT_CUT_IN_APPEAR");
        Game.fire(GameEvents.eGAME_DISABLE_INPUTS, true);
        this._destroyAllOutTweens();

        for (let i = 0; i < this._tweenAllOutAttackIn.length; ++i) {
            this._alloutAttackCutIns[i].x = this._alloutAttackStartPos[i].x;
            this._alloutAttackCutIns[i].y = this._alloutAttackStartPos[i].y;
            this._alloutAttackCutIns[i].setVisible(true);

            this._tweenAllOutAttackOut[i] = Game.windows
                .getCurrentWindow()
                .createTween(this._alloutAttackCutIns[i])
                .to({ x: this._alloutAttackFinalPos[i].x, y: this._alloutAttackFinalPos[i].y }, Common.settings.cutInMoveDuration)
                .easing(GameTween.Easing.QuadraticOut)
                .onComplete(() => {});

            this._tweenAllOutAttackStop[i] = Game.windows
                .getCurrentWindow()
                .createTween(this._alloutAttackCutIns[i])
                .to({ x: this._alloutAttackOriginalPos[i].x, y: this._alloutAttackOriginalPos[i].y }, Common.settings.cutInStopDuration)
                .easing(GameTween.Easing.QuadraticOut)
                .onComplete(() => {
                    this._tweenAllOutAttackOut[i].start();
                });

            this._tweenAllOutAttackIn[i] = Game.windows
                .getCurrentWindow()
                .createTween(this._alloutAttackCutIns[i])
                .to({ x: this._alloutAttackOriginalPos[i].x, y: this._alloutAttackOriginalPos[i].y }, Common.settings.cutInMoveDuration)
                .easing(GameTween.Easing.QuadraticOut)
                .onComplete(() => {
                    this._tweenAllOutAttackStop[i].start();
                });

            this._tweenAllOutAttackIn[i].start();
        }

        this._imgAllOutBackground.x = this._alloutBackgroundStartPos.x;
        this._imgAllOutBackground.y = this._alloutBackgroundStartPos.y;
        this._imgAllOutBackground.setVisible(true);

        /** @private @type {GameTween} */
        this._tweenAllOutBackgroundCutOut = Game.windows
            .getCurrentWindow()
            .createTween(this._imgAllOutBackground)
            .to({ x: this._alloutBackgroundFinalPos.x, y: this._alloutBackgroundFinalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._onAllOutAttackComplete();
            });

        /** @private @type {GameTween} */
        this._tweenAllOutBackgroundStop = Game.windows
            .getCurrentWindow()
            .createTween(this._imgAllOutBackground)
            .to({ x: this._alloutBackgroundOriginalPos.x, y: this._alloutBackgroundOriginalPos.y }, Common.settings.cutInStopDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._tweenAllOutBackgroundCutOut.start();
            });

        /** @private @type {GameTween} */
        this._tweenAllOutBackgroundCutIn = Game.windows
            .getCurrentWindow()
            .createTween(this._imgAllOutBackground)
            .to({ x: this._alloutBackgroundOriginalPos.x, y: this._alloutBackgroundOriginalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._tweenAllOutBackgroundStop.start();
            });

        this._tweenAllOutBackgroundCutIn.start();
    }

    /**
     * @private
     */
    _onAllOutAttackComplete() {
        this._imgAllOutBackground.setVisible(false);

        for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
            this._alloutAttackCutIns[i].setVisible(false);
        }

        this._destroyAllOutTweens();
        this.close();

        Game.fire(GameEvents.eGAME_ENABLE_INPUTS, true);
        Game.fire(GameEvents.eHEROES_ALL_OUT_ATTACK_CUTIN_DONE);
    }

    /**
     * @private
     */
    _onCutInComplete() {
        this._abilityCutIn.setVisible(false);
        this._lblCutInAbility.setVisible(false);

        this._destroyCutInTweens();
        this.close();

        Game.fire(GameEvents.eGAME_ENABLE_INPUTS, true);
    }

    /**
     * @private
     */
    _destroyCutInTweens() {
        if (this._tweenHeroCutIn) {
            this._tweenHeroCutIn.destroy();
            this._tweenHeroCutIn = null;
        }

        if (this._timerHeroCutStop) {
            this._timerHeroCutStop.destroy();
            this._timerHeroCutStop = null;
        }

        if (this._tweenHeroCutOut) {
            this._tweenHeroCutOut.destroy();
            this._tweenHeroCutOut = null;
        }

        if (this._timerTextDelay) {
            this._timerTextDelay.destroy();
            this._timerTextDelay = null;
        }

        if (this._tweenTextCutIn) {
            this._tweenTextCutIn.destroy();
            this._tweenTextCutIn = null;
        }

        if (this._timerTextCutStop) {
            this._timerTextCutStop.destroy();
            this._timerTextCutStop = null;
        }

        if (this._tweenTextCutOut) {
            this._tweenTextCutOut.destroy();
            this._tweenTextCutOut = null;
        }
    }

    /**
     * @private
     */
    _destroyAllOutTweens() {
        if (this._tweenAllOutBackgroundCutIn) {
            this._tweenAllOutBackgroundCutIn.destroy();
            this._tweenAllOutBackgroundCutIn = null;
        }

        if (this._tweenAllOutBackgroundCutOut) {
            this._tweenAllOutBackgroundCutOut.destroy();
            this._tweenAllOutBackgroundCutOut = null;
        }

        if (this._tweenAllOutBackgroundStop) {
            this._tweenAllOutBackgroundStop.destroy();
            this._tweenAllOutBackgroundStop = null;
        }

        for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
            if (this._tweenAllOutAttackIn[i]) {
                this._tweenAllOutAttackIn[i].destroy();
                this._tweenAllOutAttackIn[i] = null;
            }

            if (this._tweenAllOutAttackOut[i]) {
                this._tweenAllOutAttackOut[i].destroy();
                this._tweenAllOutAttackOut[i] = null;
            }

            if (this._tweenAllOutAttackStop[i]) {
                this._tweenAllOutAttackStop[i].destroy();
                this._tweenAllOutAttackStop[i] = null;
            }
        }
    }

    /**
     * @private
     * @param {Boolean} active
     */
    _onCheatAllOutComposition(active) {
        if (!this._imgAllOutBackground) return;

        this._cheatAllOutCompositionActive = active;
        this._imgAllOutBackground.visible = active;

        if (active) {
            this._cheatAllOutCompositionSlot = 0;
            this._cheatAllOutCompositionHeroId = HEROES_NAME_TO_ID[this._heroes[this._cheatAllOutCompositionSlot].name];
            this._imgAllOutBackground.x = this._alloutBackgroundOriginalPos.x;
            this._imgAllOutBackground.y = this._alloutBackgroundOriginalPos.y;

            for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
                this._alloutAttackCutIns[i].x = this._alloutAttackOriginalPos[i].x;
                this._alloutAttackCutIns[i].y = this._alloutAttackOriginalPos[i].y;
                this._alloutAttackCutIns[i].setVisible(true);
            }
        } else {
            this._imgAllOutBackground.x = this._alloutBackgroundStartPos.x;
            this._imgAllOutBackground.y = this._alloutBackgroundStartPos.y;

            for (let i = 0; i < this._alloutAttackCutIns.length; ++i) {
                this._alloutAttackCutIns[i].x = this._alloutAttackStartPos[i].x;
                this._alloutAttackCutIns[i].y = this._alloutAttackStartPos[i].y;
                this._alloutAttackCutIns[i].setVisible(false);
            }

            this._resetAllOutSprites();
        }
    }

    /**
     * @private
     * @param {String} keyCode
     */
    _onCheatAllOutCompositionChange(keyCode) {
        if (!this._imgAllOutBackground || !this._cheatAllOutCompositionActive) return;

        let sprite = null;
        let changeASprite = false;

        switch (keyCode) {
            // Select the cut-in on top of the currently selected cut-in.
            case KeyboardKey.eKEY_W:
                --this._cheatAllOutCompositionSlot;

                if (this._cheatAllOutCompositionSlot < 0) {
                    this._cheatAllOutCompositionSlot = TEAM_SIZE - 1;
                }

                this._cheatAllOutCompositionHeroId = HEROES_NAME_TO_ID[this._heroes[this._cheatAllOutCompositionSlot].name];
                break;
            // Select the cut-in under the currently selected cut-in.
            case KeyboardKey.eKEY_S:
                ++this._cheatAllOutCompositionSlot;

                if (this._cheatAllOutCompositionSlot >= TEAM_SIZE) {
                    this._cheatAllOutCompositionSlot = 0;
                }

                this._cheatAllOutCompositionHeroId = HEROES_NAME_TO_ID[this._heroes[this._cheatAllOutCompositionSlot].name];
                break;
            // Change the current sprite to the previous hero id's sprite
            case KeyboardKey.eKEY_A:
                changeASprite = true;
                --this._cheatAllOutCompositionHeroId;

                if (this._cheatAllOutCompositionHeroId < 0) {
                    this._cheatAllOutCompositionHeroId = NB_HEROES - 1;
                }
                break;
            // Change the current sprite to the next hero id's sprite
            case KeyboardKey.eKEY_D:
                changeASprite = true;
                ++this._cheatAllOutCompositionHeroId;

                if (this._cheatAllOutCompositionHeroId >= NB_HEROES) {
                    this._cheatAllOutCompositionHeroId = 0;
                }
                break;
        }

        if (changeASprite) {
            sprite = PIXI.Texture.from("IMG_" + HEROES_ID_TO_NAME[this._cheatAllOutCompositionHeroId] + "AllOut.png");
            this._alloutAttackCutIns[this._cheatAllOutCompositionSlot].setSprite(
                "IMG_" + HEROES_ID_TO_NAME[this._cheatAllOutCompositionHeroId] + "AllOut.png"
            );
            this._alloutAttackCutIns[this._cheatAllOutCompositionSlot].setSize(sprite.width, sprite.height);
        }
    }
}
